/**
 * Custom useTranslation hook with namespace-level type safety
 *
 * Validates namespace usage while allowing flexible keys within namespaces.
 * Best practice for dynamic backend translations.
 */

import { useMemo, useCallback } from 'react'

import type { i18n as I18nInstance } from 'i18next'
import { useTranslation as useI18nextTranslation } from 'react-i18next'

// Import namespace type from our declarations
import { STORAGE_KEYS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

import { getAvailableLanguages } from '../config'
import type { Namespace } from '../i18next.d.ts'

// Translation function type - accepts any string key with optional interpolation
// Supports: t(key), t(key, options), t(key, defaultValue, options)
type TranslationFunction = (
  key: string,
  optionsOrDefault?: Record<string, unknown> | string,
  options?: Record<string, unknown>,
) => string

type UseTranslationReturn = {
  t: TranslationFunction
  i18n: I18nInstance
  ready: boolean
}

/**
 * The `t` a helper receives as a parameter. Both the `t` this hook hands out and the `t` of react-i18next are
 * assignable to it (the two overloads are the ones `platform/i18n/i18next.d.ts` declares for i18next), so a helper
 * typed with it serves either caller. A narrower hand-written signature is not: it would refuse one of the two.
 */
export interface TFunction {
  (key: string, options?: Record<string, unknown>): string
  (key: string, defaultValue: string, options?: Record<string, unknown>): string
}

/**
 * Enhanced useTranslation hook with namespace-level type safety
 *
 * @param namespace - Translation namespace (common, auth, transactions, etc.)
 * @param options - Additional options for hook behavior
 * @returns Translation object with typed namespace
 */
export function useTranslation(
  namespace?: Namespace | Namespace[],
  options?: {
    keyPrefix?: string
    useSuspense?: boolean
    bindI18n?: string
    bindI18nStore?: string
  },
): UseTranslationReturn {
  // Cast to string to satisfy react-i18next internal types
  const ns = (namespace ?? 'common') as string

  const result = useI18nextTranslation(ns, {
    // Suspense OFF by default — matching `react.useSuspense: false` in
    // platform/i18n/config.ts. Forcing it ON here made every component throw a
    // promise while its namespace was still in flight; components mounted
    // outside a <Suspense> boundary (SessionExpiredHandler, providers, the
    // public routes' own fallbacks) then rendered NOTHING, which is how a
    // single failed namespace fetch blanked `#root` on /login. Rendering with
    // the bundled fallback / humanized keys always beats a blank page.
    useSuspense: options?.useSuspense ?? false,
    bindI18n: options?.bindI18n ?? 'languageChanged loaded',
    keyPrefix: options?.keyPrefix,
  })

  const originalT = result.t
  const i18nInstance = result.i18n
  const ready = result.ready

  // Wrap translation function to ensure string return
  const t = useCallback(
    (
      key: string,
      optionsOrDefaultValue?: Record<string, unknown> | string,
      optionsParam?: Record<string, unknown>,
    ): string => {
      try {
        // Handle 3-argument pattern: t(key, defaultValue, options)
        // When second arg is a string (defaultValue) and third arg is options object
        if (typeof optionsOrDefaultValue === 'string' && optionsParam !== undefined) {
          // This is the t(key, defaultValue, options) pattern.
          // Merge the defaultValue INTO the options so i18next uses it as the
          // native fallback (and interpolates it) when the key is missing. The
          // project's missingKeyHandler only logs and never returns, so without
          // this merge i18next returns the raw key and the English fallback of
          // every 3-arg call site (admin pagination, export modal, etc.) is
          // dead code — users would see 'superAdmin.orgAdmin...' instead.
          const merged: Record<string, unknown> = {
            defaultValue: optionsOrDefaultValue,
            ...(optionsParam ?? {}),
          }
          const result = originalT(key as never, merged as never)
          return typeof result === 'string' ? result : optionsOrDefaultValue
        }
        // Handle t(key) and t(key, options) signatures
        const result = originalT(key as never, optionsOrDefaultValue as never)
        return typeof result === 'string' ? result : key
      } catch {
        // Fallback to defaultValue if provided, otherwise return key
        return typeof optionsOrDefaultValue === 'string' ? optionsOrDefaultValue : key
      }
    },
    [originalT],
  )

  return useMemo(
    () => ({
      t,
      i18n: i18nInstance,
      ready,
    }),
    [t, i18nInstance, ready],
  )
}

/**
 * Hook for language management operations.
 *
 * Issue #330: `availableLanguages` is now derived from the DB-backed catalog
 * fetched on i18n init (`getAvailableLanguages()`), with a static fallback
 * `{ es: 'Español', en: 'English' }` if the API call failed.
 *
 * Returns a stable shape: `availableLanguages` is a `Record<string, string>`
 * keyed by code → label, matching the legacy contract used by
 * `LanguageSelector`.
 */
export function useLanguage() {
  const { i18n } = useI18nextTranslation()

  const currentLanguage = useMemo(() => i18n.language, [i18n.language])

  /**
   * Code → label map sourced from the dynamic catalog. Memoised on the
   * underlying list reference so React re-renders correctly after the
   * cache is hydrated by `initializeI18n()`.
   */
  const availableLanguages = useMemo<Record<string, string>>(() => {
    const list = getAvailableLanguages()
    return Object.fromEntries(list.map((entry) => [entry.code, entry.label]))
    // Recompute when i18n.language changes — that's a reasonable trigger for
    // re-reading the cache (e.g. after language switch the user might want
    // updated labels). The list itself is module-scoped and stable until
    // `clearAvailableLanguagesCache()` is called.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language is an intentional recompute trigger for the module-scoped catalog cache (see comment above)
  }, [i18n.language])

  const changeLanguage = useCallback(
    async (lng: string): Promise<void> => {
      try {
        await i18n.changeLanguage(lng)

        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEYS.LANGUAGE, lng)
          document.documentElement.lang = lng
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          logger.error(`[i18n] Failed to change language to: ${lng}`, error)
        }
        throw error
      }
    },
    [i18n],
  )

  const isLoading = useMemo(() => !i18n.isInitialized, [i18n.isInitialized])

  return useMemo(
    () => ({
      currentLanguage,
      availableLanguages,
      changeLanguage,
      isLoading,
    }),
    [currentLanguage, availableLanguages, changeLanguage, isLoading],
  )
}

/**
 * Hook for namespace management and lazy loading
 *
 * @param namespace - Namespace to ensure is loaded
 * @returns Loading state for namespace
 */
export function useNamespace(namespace: string) {
  const { i18n } = useI18nextTranslation()

  const loadNamespace = useCallback(
    async (ns: string): Promise<void> => {
      try {
        await i18n.loadNamespaces([ns])

        if (process.env.NODE_ENV === 'development') {
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          logger.error(`[i18n] Failed to load namespace: ${ns}`, error)
        }
        throw error
      }
    },
    [i18n],
  )

  const isLoaded = useMemo(
    () => i18n.hasResourceBundle(i18n.language, namespace),
    [i18n, namespace],
  )

  return useMemo(
    () => ({
      loadNamespace,
      isLoaded,
      namespace,
    }),
    [loadNamespace, isLoaded, namespace],
  )
}

/**
 * Hook for pluralization with crypto-specific counts
 *
 * @param namespace - Namespace for translations
 * @returns Pluralization utilities
 */
export function usePluralization(namespace: Namespace = 'common') {
  const { t } = useTranslation(namespace)

  const plural = useCallback(
    (key: string, count: number, options?: Record<string, unknown>): string => {
      return t(key, {
        count,
        ...options,
      })
    },
    [t],
  )

  const currency = useCallback(
    (amount: number, asset: string, _options?: Record<string, unknown>): string => {
      return `${amount.toLocaleString()} ${asset}`
    },
    [],
  )

  const percentage = useCallback((value: number, _options?: Record<string, unknown>): string => {
    return `${(value * 100).toFixed(2)}%`
  }, [])

  return useMemo(
    () => ({
      plural,
      currency,
      percentage,
    }),
    [plural, currency, percentage],
  )
}

/**
 * Hook for development and debugging utilities
 * Only available in development mode
 */
export function useI18nDebug() {
  const { i18n } = useI18nextTranslation()

  const logMissingKeys = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[i18n Debug] Missing translation keys')
      // This would require additional setup to track missing keys
    }
  }, [])

  const logLoadedNamespaces = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[i18n Debug] Loaded namespaces')
    }
  }, [])

  const validateTranslations = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      // Basic validation - check if translations load correctly
      const testKeys = [
        'common:navigation.transactions',
        'common:actions.save',
        'common:status.success',
      ]

      logger.debug('[i18n Debug] Translation validation')
      testKeys.forEach((key) => {
        const [ns, k] = key.split(':')
        const value = (
          i18n as import('i18next').i18n & {
            getResource?: (lng: string, ns: string, key: string) => unknown
          }
        ).getResource?.(i18n.language, ns, k)
        // Surface the validation result (DEV-gated logger); undefined = missing key
        logger.debug(`[i18n Debug] ${key}:`, value === undefined ? 'MISSING' : value)
      })
    }
  }, [i18n])

  return useMemo(
    () => ({
      logMissingKeys,
      logLoadedNamespaces,
      validateTranslations,
    }),
    [logMissingKeys, logLoadedNamespaces, validateTranslations],
  )
}
